import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function CategoryManager() {
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", image: "", color: "#9F4835", order: 0 });

  const load = async () => { try { setCategories((await api.get("/categories")).data); } catch {} };
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    try {
      if (editing) { await api.put(`/categories/${editing}`, form); toast.success("Catégorie mise à jour"); }
      else { await api.post("/categories", form); toast.success("Catégorie créée"); }
      setOpen(false); setEditing(null); setForm({ name: "", description: "", image: "", color: "#9F4835", order: 0 }); load();
    } catch { toast.error("Erreur"); }
  };

  const handleEdit = (cat) => { setForm({ name: cat.name, description: cat.description, image: cat.image, color: cat.color, order: cat.order }); setEditing(cat.id); setOpen(true); };
  const handleDelete = async (id) => { if (!window.confirm("Supprimer ?")) return; try { await api.delete(`/categories/${id}`); toast.success("Supprimée"); load(); } catch {} };

  return (
    <div data-testid="category-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl">Catégories</h1>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm({ name: "", description: "", image: "", color: "#9F4835", order: 0 }); } }}>
          <DialogTrigger asChild><Button className="rounded-none" data-testid="new-category-btn"><Plus className="h-4 w-4 mr-2" /> Nouvelle catégorie</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifier" : "Nouvelle"} catégorie</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-none mt-1" data-testid="category-name-input" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="rounded-none mt-1" data-testid="category-desc-input" /></div>
              <div><Label>Image (URL)</Label><Input value={form.image} onChange={(e) => setForm({...form, image: e.target.value})} className="rounded-none mt-1" data-testid="category-image-input" /></div>
              <div className="flex gap-4">
                <div className="flex-1"><Label>Couleur</Label><Input type="color" value={form.color} onChange={(e) => setForm({...form, color: e.target.value})} className="h-10 mt-1" /></div>
                <div className="flex-1"><Label>Ordre</Label><Input type="number" value={form.order} onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})} className="rounded-none mt-1" /></div>
              </div>
              <Button onClick={handleSave} className="w-full rounded-none" data-testid="save-category-btn">{editing ? "Mettre à jour" : "Créer"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="border rounded-none">
        <Table>
          <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Articles</TableHead><TableHead>Ordre</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {categories.map((cat) => (
              <TableRow key={cat.id}>
                <TableCell><div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full" style={{backgroundColor: cat.color}} /><span className="font-medium">{cat.name}</span></div></TableCell>
                <TableCell>{cat.article_count}</TableCell>
                <TableCell>{cat.order}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(cat)} data-testid={`edit-cat-${cat.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(cat.id)} data-testid={`delete-cat-${cat.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
